import { ShieldCheck } from "lucide-react";
import { StepHeading } from "@/components/booking/OptionCard";
import { PromoCode, PriceBreakdown } from "@/components/booking/PromoCode";
import { TIME_SLOTS } from "@/data/content";
import { gbp, formatDate } from "@/lib/booking";

export function SummaryList({ rows, testId }) {
  return (
    <dl className="divide-y divide-subtle rounded-2xl border border-subtle" data-testid={testId}>
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[110px_1fr] sm:grid-cols-[150px_1fr] gap-4 px-5 py-3.5 text-sm">
          <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-faint pt-0.5">{k}</dt>
          <dd className="text-ink break-words" data-testid={`summary-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function StepReview({ form, q, catalog, promo, setPromo, autoCode, onPromoRemove }) {
  const freq = catalog.frequencies.find((f) => f.value === form.frequency)?.label;
  const slot = TIME_SLOTS.find((t) => t.value === form.time_slot);
  const rows = [
    ["Service", `${q.service.name} — ${gbp(q.base)}`],
    ["Property", q.property.label],
    ...(q.service.has_frequency ? [["Frequency", freq]] : []),
    ["Extras", q.extras.length ? q.extras.map((e) => `${e.name} (+${gbp(e.price)})`).join(", ") : "None"],
    ["Date", formatDate(form.date)],
    ["Preferred time", slot ? `${slot.label}, ${slot.hint} (arrival window)` : ""],
    ["Address", `${form.address}, ${form.postcode.toUpperCase()}`],
    ["Name", form.name],
    ["Contact", `${form.email} · ${form.phone}`],
  ];
  return (
    <div data-testid="booking-review">
      <StepHeading title="Review your booking." />
      <div className="mt-6">
        <SummaryList rows={rows} testId="booking-summary" />
      </div>
      <PromoCode form={form} promo={promo} setPromo={setPromo} autoCode={autoCode} onRemove={onPromoRemove} />
      {promo && <PriceBreakdown q={q} promo={promo} />}
      <div className="mt-5 flex items-baseline justify-between rounded-2xl bg-blue px-6 py-5 text-canvas">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-canvas/70">{promo ? "Final total" : "Total price"}</span>
        <span className="font-serif text-4xl" data-testid="booking-total">{gbp(promo ? promo.total_price : q.total)}</span>
      </div>
      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-sage/40 bg-sage-light/50 p-5">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-sage-dark" />
        <p className="text-sm leading-relaxed text-body">
          This is a booking request, not a confirmed booking. We'll check availability and contact you to confirm or suggest
          an alternative. Once confirmed, we'll email you a secure Stripe link to pay this exact amount. No payment is taken now, and no VAT is added.
        </p>
      </div>
    </div>
  );
}

export function PriceBar({ q }) {
  if (!q.property) return null;
  return (
    <div className="mt-8 flex items-center justify-between border-t border-subtle pt-5" data-testid="booking-price-bar">
      <span className="text-xs text-faint">
        {q.service.name} · {q.property.label}
        {q.extras.length > 0 && ` · ${q.extras.length} extra${q.extras.length > 1 ? "s" : ""}`}
      </span>
      <span className="font-serif text-2xl text-blue" data-testid="booking-running-total">{gbp(q.total)}</span>
    </div>
  );
}
